import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CENTER_DB_MAPPINGS } from '@shared/db-fields-mappings/center-db.mappings';
import { COMPETITORS_SALES_MAPPINGS } from '@shared/db-fields-mappings/competitors-sales.mappings';
import { HCP_DB_MAPPINGS } from '@shared/db-fields-mappings/hcp-db.mappings';
import { PRODUCT_USE_IMPORT_MAPPINGS } from '@shared/db-fields-mappings/product-use.mappings';
import { REGION_BRICKS_MAPPINGS } from '@shared/db-fields-mappings/region-bricks.mappings';
import { Endpoints, ImportEndpoints } from '@shared/Endpoints';
import { ImportArea } from '@shared/enums/SearchArea';
import { TDBFieldMappingItem } from '@shared/interfaces/TDBFieldMappingItem';
import { TImportMappingItem } from '@shared/interfaces/TImportMappingItem';
import { isNullOrEmpty } from '@shared/utils/isNullOrEmpty';
import { isNullOrUndefined } from '@shared/utils/isNullOrUndefined';
import { environment } from 'src/environments/environment';
import { CSVUtils } from 'src/utils/csvUtils';
import { CategoriesService } from '../../services/categories.service';
import { LoadingService, TLoadingItem } from '../../services/loading.service';

export interface TImportData {
  headers: string[];
  area: ImportArea;
  projectId: string;
  //file: File;
  e: Event;
  competitor?: string;
  importCallback: ((e: Event, mappings: TImportMappingItem[]) => void) | undefined;
}

export interface TImportItem {
  id: string;
  name: string;
  //position: number;
}

@Component({
  selector: 'app-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.scss'],
})
export class ImportComponent implements OnInit {
  public importedFileHeaders: TImportItem[] = [];
  public localHeaders: TImportItem[] = [];
  public notMatchedColumns: TImportItem[] = [];
  public validationResults: string[] = [];
  public validated: boolean = false;
  public fileName: string = '';

  constructor(
    public dialogRef: MatDialogRef<ImportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TImportData,
    private _categoriesService: CategoriesService,
    private _loadingService: LoadingService,
    private _http: HttpClient
  ) {}

  public ngOnInit(): void {
    console.log(`ImportComponent initialized with data:`, this.data);
    this._buildLocalHeaders(this.data.area);
    this._buildImportedFileHeaders();
  }

  public dropToNotMatched(event: CdkDragDrop<TImportItem[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const oldItem = structuredClone(this.importedFileHeaders[event.previousIndex]);
      this.importedFileHeaders[event.previousIndex] = { id: '', name: '' };
      this.notMatchedColumns[event.currentIndex] = oldItem;
    }
  }

  public dropToMapped(event: CdkDragDrop<TImportItem[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      const newItem = event.previousContainer.data[event.previousIndex];
      const oldItem = this.importedFileHeaders[event.currentIndex];

      this.importedFileHeaders[event.currentIndex] = newItem;
      if (isNullOrEmpty(oldItem.id)) {
        this.notMatchedColumns.splice(event.previousIndex, 1);
      } else {
        this.notMatchedColumns[event.previousIndex] = oldItem;
      }
    }
  }

  public async validate(): Promise<void> {
    const target: HTMLInputElement = <HTMLInputElement>this.data.e.target;
    const files: FileList | null = target.files;
    const endpoint = this._getValidateEndpoint();
    const url: string = Endpoints.BUILD_URL('import', {
      base: environment.apiUrl,
      endpoint,
      params: '',
    });
    const mappings: TImportMappingItem[] = this.localHeaders.map((header, index) => {
      return { from: this.importedFileHeaders[index].id, to: header.id };
    }).filter((it) => !isNullOrEmpty(it.from) && !isNullOrEmpty(it.to));
    const loadingItem: TLoadingItem = this._loadingService.buildLoadingItem(`Validating data...`);
    this._loadingService.addLoadingItem(loadingItem);
    const formData = await CSVUtils.getImportFormData(files![0], this.data.projectId, mappings);
    const response = this._http.post<string[]>(url, formData);
    response.subscribe((res) => {
      this.validationResults = res;
    });
    this._loadingService.removeItem(loadingItem);
    this._loadingService.triggerOkMessage(`Data validated`);
    this.validated = true;
  }

  private _getValidateEndpoint(): ImportEndpoints {
    switch (this.data.area) {
      case ImportArea.Centers:
        return 'validateCenters';
      case ImportArea.HCP:
        return 'validateHCPs';
      case ImportArea.ProductSales:
        return 'validateProductSales';
      case ImportArea.RegionBricks:
        return 'validateRegionBricks';
      case ImportArea.CompetitorsSales:
        return 'validateCompetitors';
      case ImportArea.CompetitorsSalesInEuro:
        return 'validateCompetitors';
      case ImportArea.CompetitorsPatients:
        return 'validateCompetitors';
      default:
        throw new Error(`Unknown import area: ${this.data.area}`);
    }
  }

  public import(): void {
    if (!isNullOrUndefined(this.data.importCallback)) {
      const mappings: TImportMappingItem[] = this.localHeaders.map((header, index) => {
        return { from: this.importedFileHeaders[index].id, to: header.id };
      }).filter((it) => !isNullOrEmpty(it.from) && !isNullOrEmpty(it.to));
      this.data.importCallback(this.data.e, mappings);
      this.dialogRef.close();
    }
  }

  private _buildImportedFileHeaders(): void {
    const notFoundHeaders: TImportItem[] = [];
    const importedFileHeaders: TImportItem[] = this.localHeaders.map(() => ({ id: '', name: '' }));
    this.data.headers.forEach((header) => {
      const localHeaderIndex: number = this.localHeaders.findIndex((it) => it.id === header);
      if (localHeaderIndex > -1) {
        importedFileHeaders[localHeaderIndex].id = header;
      } else {
        notFoundHeaders.push({ id: header, name: '' });
      }
    });
    this.importedFileHeaders = importedFileHeaders;
    this.notMatchedColumns = notFoundHeaders;
  }

  private _buildLocalHeaders(area: ImportArea): void {
    let mappings: Map<string, TDBFieldMappingItem> = new Map();
    switch (area) {
      case ImportArea.Centers:
        mappings = CENTER_DB_MAPPINGS;
        break;
      case ImportArea.HCP:
        mappings = HCP_DB_MAPPINGS;
        break;
      case ImportArea.ProductSales:
        mappings = PRODUCT_USE_IMPORT_MAPPINGS;
        break;
      case ImportArea.RegionBricks:
        mappings = REGION_BRICKS_MAPPINGS;
        break;
      case ImportArea.CompetitorsSales:
        mappings = COMPETITORS_SALES_MAPPINGS;
        break;
    }
    const target: HTMLInputElement = <HTMLInputElement>this.data.e.target;
    if (!isNullOrUndefined(target.files)) {
      this.fileName = target.files[0].name;
    }
    this.localHeaders = [...mappings.values()].filter((it) => it.export === true && it.import !== false).map((it, position) => {
      return {
        name: this._categoriesService.getFieldDescription(
          it.fieldId,
          this.data.projectId
        ),
        id: it.fieldId,
        position,
      };
    });
  }
}
